import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import dayjs from "dayjs";
import { useAuth } from "../../../context/AuthContext";

export const CIUDADES = ["La Paz", "La Paz(El Alto)", "Santa Cruz", "Cochabamba", "Beni", "Pando", "Chuquisaca", "Potosi", "Tarija", "Oruro"];

// El perfil del usuario (useAuth().user) se carga async después del montaje (AuthContext
// hace fetchProfile en un useEffect), así que en el primer render `user` todavía es null:
// un useState(initialValues) que lea user?.sucursal ahí nunca se actualiza solo. Por eso la
// ciudad por defecto se aplica con un useEffect que espera a que el perfil llegue.
export const normalizeCiudad = (value) => {
  const found = CIUDADES.find((c) => c.toLowerCase() === String(value || "").trim().toLowerCase());
  return found || "";
};

const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 12.5 },
  "& .MuiOutlinedInput-root": { fontSize: 13 },
};

const parseErrorMessage = (err, idLabel) => {
  const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "";
  if (/duplicate entry/i.test(msg)) return `Ya existe un equipo registrado con ese ${idLabel}.`;
  return msg || "No se pudo registrar el equipo.";
};

// Formulario genérico para los 5 tipos de equipo serializado (equipo_modem, equipo_radio,
// equipo_varios, equipo_chip, equipo_bateria): todos comparten la misma forma (un
// identificador único + modelo/marca + ciudad + observación), solo cambian los campos
// puntuales de cada tabla, así que en vez de 5 archivos casi idénticos se parametriza uno solo.
export default function EquipoSerializadoForm({ titulo, idField, fields, crear }) {
  const { user } = useAuth();
  const initialValues = { [idField.name]: "", ciudad: normalizeCiudad(user?.sucursal) };
  fields.forEach((f) => {
    initialValues[f.name] = "";
  });

  const [values, setValues] = useState(initialValues);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    if (!user?.sucursal) return;
    setValues((prev) => (prev.ciudad ? prev : { ...prev, ciudad: normalizeCiudad(user.sucursal) }));
  }, [user?.sucursal]);

  const setValue = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

  const requiredMissing =
    !String(values[idField.name] || "").trim() ||
    !String(values.ciudad || "").trim() ||
    fields.some((f) => f.required && !String(values[f.name] || "").trim());

  const limpiar = () => setValues(initialValues);

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      const payload = { estado: "Almacen", fecha_registro: dayjs().format("YYYY-MM-DD"), usu: user?.username };
      payload[idField.name] = String(values[idField.name]).trim();
      payload.ciudad = values.ciudad;
      fields.forEach((f) => {
        const raw = values[f.name];
        if (raw === "" || raw === undefined) return;
        payload[f.name] = f.type === "number" ? Number(raw) : raw;
      });

      await crear(payload);
      limpiar();
      setOpenSnackbar(true);
    } catch (err) {
      setError(parseErrorMessage(err, idField.label));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, maxWidth: 720 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.25 }}>
        {titulo}
      </Typography>

      {!!error && (
        <MuiAlert severity="error" variant="outlined" sx={{ mb: 1.25, fontSize: 12.5 }}>
          {error}
        </MuiAlert>
      )}

      <Grid container spacing={1.25}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label={idField.label}
            value={values[idField.name]}
            onChange={(e) => setValue(idField.name, e.target.value)}
            required
            fullWidth
            size="small"
            sx={controlSx}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required sx={controlSx}>
            <InputLabel>Ciudad</InputLabel>
            <Select label="Ciudad" value={values.ciudad} onChange={(e) => setValue("ciudad", e.target.value)}>
              {CIUDADES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {fields.map((f) => (
          <Grid key={f.name} size={{ xs: 12, sm: f.multiline ? 12 : 6 }}>
            <TextField
              label={f.label}
              value={values[f.name]}
              onChange={(e) => setValue(f.name, e.target.value)}
              required={!!f.required}
              type={f.type === "number" ? "number" : "text"}
              multiline={!!f.multiline}
              minRows={f.multiline ? 3 : undefined}
              fullWidth
              size="small"
              sx={controlSx}
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          disabled={requiredMissing || guardando}
          onClick={guardar}
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Registrar
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Equipo registrado correctamente.
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
}
